import { useCallback, useEffect, useState } from 'react';
import type { AdminSeatRow } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface SeatsState {
  status: 'loading' | 'ready' | 'error';
  seats: AdminSeatRow[];
  error: ApiError | null;
  reload: () => void;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** Loads guest seats for a sponsor. `userId` is required on the subscription row. */
export function useSeats(sponsorUserId: number): SeatsState {
  const [status, setStatus] = useState<SeatsState['status']>('loading');
  const [seats, setSeats] = useState<AdminSeatRow[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const api = useTenantApi();

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    api.listSeats({ sponsorUserId }).then(
      (next) => {
        setSeats(next.seats);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setSeats([]);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api, sponsorUserId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { status, seats, error, reload };
}
